import { useEffect, useMemo, useState } from 'react'
import { readableError } from '@solum/format'
import { Button, Notice, Select } from '../controls'
import { FactList } from '../work/FactList'
import { PageSection } from './PageSection'

export interface RecoverySurfaceContext {
  principalClass: 'console' | 'portal'
  accountId: string
  portalId: string | null
  accountName: string
  portalName: string | null
}

export interface RecoverySurfaceCase {
  id: string
  state: string
  principalClass: 'console' | 'portal'
  accountId: string
  portalId: string | null
  createdAt: string
  expiresAt: string
  lastFailureCode?: string | null
}

export interface RecoverySurfaceProps {
  contexts: RecoverySurfaceContext[]
  cases: RecoverySurfaceCase[]
  busy?: boolean
  error?: string | null
  onRequest: (context: RecoverySurfaceContext) => void
  onAdvance: (recoveryCase: RecoverySurfaceCase) => void
}

const terminal = new Set(['completed', 'rejected', 'expired', 'cancelled', 'failed'])

const copy: Record<string, string> = {
  requested: 'A recovery custodian must verify your identity evidence.',
  evidence_ready: 'Your evidence is verified. Freeze the current methods and request the independent decision.',
  pending_approval: 'A different recovery custodian must approve from a fresh two-factor session.',
  approved: 'The independent decision is ready. Reset the approved methods and sign out everywhere.',
  executing: 'The provider operation is incomplete. Retry from this same recovery case.',
  completed: 'Recovery is complete. Sign in again, then enrol fresh two-factor authentication and recovery codes.',
  rejected: 'The independent decision was rejected. You can start a new recovery case if needed.',
  expired: 'This recovery case expired. Start a new one if needed.',
  cancelled: 'This recovery case was cancelled. Start a new one if needed.',
  failed: 'Recovery failed closed. Start a new case or contact a recovery custodian.',
}

const sameContext = (c: RecoverySurfaceCase, x: RecoverySurfaceContext) =>
  c.principalClass === x.principalClass && c.accountId === x.accountId && c.portalId === x.portalId

export function RecoverySurface({ contexts, cases, busy = false, error, onRequest, onAdvance }: RecoverySurfaceProps) {
  const options = useMemo(() => contexts.map((context) => ({
    value: `${context.principalClass}:${context.accountId}:${context.portalId ?? '-'}`,
    label: context.portalName ? `${context.accountName} · ${context.portalName}` : `${context.accountName} · Console`,
  })), [contexts])
  const [choice, setChoice] = useState(options[0]?.value ?? '')
  useEffect(() => {
    if (!options.some((option) => option.value === choice)) setChoice(options[0]?.value ?? '')
  }, [choice, options])
  const selected = contexts.find((context) =>
    `${context.principalClass}:${context.accountId}:${context.portalId ?? '-'}` === choice) ?? contexts[0]
  const current = selected ? cases.find((row) => sameContext(row, selected) && !terminal.has(row.state))
    ?? cases.find((row) => sameContext(row, selected)) : undefined
  const canRequest = !!selected && (!current || terminal.has(current.state))
  const canAdvance = current?.state === 'evidence_ready' || current?.state === 'approved' || current?.state === 'executing'
  const action = current?.state === 'evidence_ready'
    ? 'Continue recovery'
    : current?.state === 'approved' || current?.state === 'executing'
      ? 'Reset factors and sign out'
      : 'Request factor recovery'

  return (
    <PageSection title="Factor recovery" headingLevel={3} testId="factor-recovery"
      summary="A recovery changes only the methods approved by two different custodians. Marble never stores a factor secret."
      primaryAction={(canRequest || canAdvance) ? (
        <Button variant={canAdvance ? 'primary' : 'secondary'} disabled={busy}
          data-testid="factor-recovery-action"
          onClick={() => current && canAdvance ? onAdvance(current) : selected && onRequest(selected)}>
          {action}
        </Button>
      ) : undefined}>
      {contexts.length > 1 ? (
        <Select value={choice} onValueChange={setChoice} options={options}
          aria-label="Recovery account and sign-in surface" testId="factor-recovery-context" />
      ) : null}
      {!selected ? <Notice tone="info">
        <strong>No recovery context.</strong> This sign-in is not attached to a live Console or Portal account.
      </Notice> : null}
      {current ? <FactList layout="rows" data-testid="factor-recovery-case" facts={[
        { key: 'status', label: 'Status', value: current.state.replaceAll('_', ' ') },
        { key: 'next', label: 'What happens next', value: copy[current.state] ?? 'Ask a recovery custodian for help.' },
        { key: 'expires', label: 'Case expires', value: current.expiresAt },
        ...(current.lastFailureCode ? [{ key: 'outcome', label: 'Last outcome', value: current.lastFailureCode }] : []),
      ]} /> : selected ? <p data-testid="factor-recovery-empty">No active recovery case.</p> : null}
      {error ? <p role="alert">{readableError(error)}</p> : null}
    </PageSection>
  )
}
